import { cn } from '@/lib/utils';
import React from 'react';
import { AspectRatioSelector } from './controls/AspectRatioSelector';
import { ModeSelector } from './controls/ModeSelector';
import { ModelSelector } from './controls/ModelSelector';
import { SecondaryControls } from './controls/SecondaryControls';

interface GeneratorControlsProps {
  user: any;
  services: any[];
  model: string;
  aspectRatio: string;
  resolution: string;
  duration: string;
  variations: string;
  mode: string;
  outputFormat: string;
  activeDropdown: string | null;
  toggleDropdown: (name: string) => void;
  selectOption: (type: string, val: string, label: string) => void;
  setOpenSection: (section: string | null) => void;
  controlsRef: React.RefObject<HTMLDivElement | null>;
  enhanceContent?: React.ReactNode;
  autoSwitchEnabled: boolean;
  selectedImage?: string | null;
  selectedImages?: string[];
}

export function GeneratorControls({
  user,
  services,
  model,
  aspectRatio,
  resolution,
  duration,
  variations,
  mode,
  outputFormat,
  activeDropdown,
  toggleDropdown,
  selectOption,
  setOpenSection,
  controlsRef,
  enhanceContent,
  autoSwitchEnabled,
  selectedImage,
  selectedImages,
}: GeneratorControlsProps) {
  const [highlightedFields, setHighlightedFields] = React.useState<Record<string, boolean>>({});
  const isDirectModelChange = React.useRef(false);

  const handleSelect = React.useCallback(
    (type: string, val: string, label: string) => {
      if (type === 'model') {
        isDirectModelChange.current = true;
      }
      selectOption(type, val, label);
    },
    [selectOption]
  );

  // Track values for highlighting. Initialize to empty object to trigger flash on mount.
  const prevValues = React.useRef<any>({});

  React.useEffect(() => {
    const isEnhanceVisible = model !== 'elevenlabs-sfx';
    const current = {
      model,
      aspectRatio,
      resolution,
      duration,
      variations,
      mode,
      outputFormat,
      isEnhanceVisible,
    };
    const changed: Record<string, boolean> = {};
    let hasChanges = false;

    Object.keys(current).forEach((key) => {
      // @ts-ignore
      if (current[key] !== prevValues.current[key]) {
        // @ts-ignore
        if (key === 'isEnhanceVisible') {
          if (isEnhanceVisible) {
            // Only highlight if it becomes visible
            changed.enhance = true;
            hasChanges = true;
          }
        } else {
          // @ts-ignore
          changed[key] = true;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      let fieldsToHighlight = changed;

      // Direct model selection forces all highlights
      if (isDirectModelChange.current) {
        fieldsToHighlight = {
          model: true,
          aspectRatio: true,
          resolution: true,
          duration: true,
          variations: true,
          mode: true,
          outputFormat: true,
          enhance: isEnhanceVisible,
        };
        isDirectModelChange.current = false;
      }

      setHighlightedFields((prev) => ({ ...prev, ...fieldsToHighlight }));
      prevValues.current = current;
    }

    // Safety reset for the ref if no changes happened (e.g. selected same model)
    if (isDirectModelChange.current) {
      isDirectModelChange.current = false;
    }
  }, [model, aspectRatio, resolution, duration, variations, mode, outputFormat]);

  // Dedicated Watchdog to reliably clear highlights after 500ms.
  // This prevents race conditions where re-renders could cancel the cleanup timer.
  React.useEffect(() => {
    const hasHighlights = Object.keys(highlightedFields).length > 0;
    if (!hasHighlights) return;

    const timer = setTimeout(() => {
      setHighlightedFields({});
    }, 500);

    return () => clearTimeout(timer);
  }, [highlightedFields]);

  const getMinCost = React.useCallback(
    (modelPrefix: string) => {
      if (!services || services.length === 0) return null;

      const related = services.filter(
        (s) => s.slug.toLowerCase().startsWith(modelPrefix.toLowerCase()) && s.isActive
      );
      if (related.length === 0) return null;

      return Math.min(...related.map((s) => s.creditCost));
    },
    [services]
  );

  return (
    <div className="controls rounded-none px-3 py-3" ref={controlsRef} data-model={model}>
      <div className="flex w-full flex-nowrap items-center justify-start md:gap-[6px]">
        <ModelSelector
          model={model}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          handleSelect={handleSelect}
          getMinCost={getMinCost}
          user={user}
          highlighted={!!highlightedFields.model}
          autoSwitchEnabled={autoSwitchEnabled}
        />

        <AspectRatioSelector
          model={model}
          aspectRatio={aspectRatio}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          handleSelect={handleSelect}
          highlighted={!!highlightedFields.aspectRatio}
          selectedImage={selectedImage}
          selectedImages={selectedImages}
        />

        <ModeSelector
          model={model}
          mode={mode}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          handleSelect={handleSelect}
          highlighted={!!highlightedFields.mode}
        />

        <SecondaryControls
          model={model}
          resolution={resolution}
          duration={duration}
          outputFormat={outputFormat}
          variations={variations}
          mode={mode}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          handleSelect={handleSelect}
          setOpenSection={setOpenSection}
          highlighted={highlightedFields}
          enhanceContent={enhanceContent}
        />
      </div>
    </div>
  );
}
